import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { ClipboardCheck, Lock } from 'lucide-react';
import stocktakeService from '../../services/nurseryStocktake';
import { useStockSeat } from '../../hooks/useStockSeat';
import { useOrgTimezone, formatDateTimeInTz } from '../../utils/timezone';
import { errorMessage } from '../../utils/apiResponse';
import { STOCKTAKE_KIND_NAME, type StocktakesResponse, type StocktakeListItem, type StocktakeKind } from '../../types/nurseryStocktake';

/**
 * /nursery/stocktake — count the lots standing at a farm, or one block of it.
 * Starting a place that already has a count open joins it, so two people can
 * take a side of the track each.
 *
 * A walk is a stocktake (is the record right?) or a step in a growing crop's
 * season: the bud, 1st and 2nd counts, which feed the forecast of what the
 * crop will give and walk only the lots still growing.
 */

const KIND_HELP: Record<StocktakeKind, string> = {
  stocktake: 'Check the record of every lot here, growing or ready.',
  bud_count: 'Count what took after budding or grafting. The roughest count: the forecast takes off the most.',
  first_count: 'The mid-season count of the growing crop.',
  second_count: 'The count close to finished. The forecast takes off the least.',
};

const kindOf = (s: { kind?: StocktakeKind }) => s.kind ?? 'stocktake';

const whereOf = (s: { farm_name: string; block_name: string | null }) =>
  s.block_name ? `${s.farm_name} · ${s.block_name}` : `${s.farm_name} · every block`;

function signed(n: number) {
  return n > 0 ? `+${n.toLocaleString()}` : n.toLocaleString();
}

function StocktakeRow({ s, tz }: { s: StocktakeListItem; tz: string }) {
  const pct = s.lots_total ? Math.min(100, Math.round((s.lots_counted / s.lots_total) * 100)) : 0;
  return (
    <li>
      <Link to={`/nursery/stocktake/${s.id}`} className="block bg-white border border-neutral-200 rounded-xl p-4 hover:border-primary-300">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="font-semibold text-neutral-900 break-words min-w-0">{whereOf(s)}</span>
          <span className="text-sm text-neutral-500">{STOCKTAKE_KIND_NAME[kindOf(s)]} {s.label}</span>
          {s.status === 'finished' && (
            <span className="text-xs rounded-full bg-neutral-100 text-neutral-600 px-2 py-0.5">Finished</span>
          )}
        </div>
        <p className="text-xs text-neutral-500 mt-1 break-words">
          {s.status === 'open'
            ? `Started ${formatDateTimeInTz(s.started_at, tz)}${s.started_by_name ? ` by ${s.started_by_name}` : ''}`
            : `Finished ${s.finished_at ? formatDateTimeInTz(s.finished_at, tz) : ''}${s.finished_by_name ? ` by ${s.finished_by_name}` : ''}`}
        </p>
        <div className="mt-2 h-2 rounded-full bg-neutral-100 overflow-hidden" aria-label={`${s.lots_counted} of ${s.lots_total} lots counted`}>
          <div className={s.status === 'open' ? 'h-full bg-green-500' : 'h-full bg-neutral-400'} style={{ width: `${pct}%` }} />
        </div>
        <p className="text-xs text-neutral-500 mt-1">
          {s.lots_counted} of {s.lots_total} lots counted
          {s.lots_counted > 0 && ` · ${s.difference === 0 ? 'no difference' : `${signed(s.difference)} trees against the record`}`}
        </p>
      </Link>
    </li>
  );
}

function Stocktakes() {
  const tz = useOrgTimezone();
  const navigate = useNavigate();
  const { hasAccess, loading: licenseLoading } = useStockSeat('count');
  const [data, setData] = useState<StocktakesResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [farmId, setFarmId] = useState<number | ''>('');
  const [blockId, setBlockId] = useState<number | ''>('');
  const [kind, setKind] = useState<StocktakeKind>('stocktake');
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    if (!hasAccess) return;
    stocktakeService.list()
      .then((res) => {
        if (!res.success) { setError(errorMessage(res, 'Could not load stocktakes')); return; }
        setData(res.data);
        if (res.data.places.length === 1) setFarmId(res.data.places[0].farm_id);
      })
      .catch((e) => setError(errorMessage(e, 'Could not load stocktakes')));
  }, [hasAccess]);

  const farm = useMemo(() => data?.places.find((p) => p.farm_id === farmId), [data, farmId]);
  const open = data?.stocktakes.filter((s) => s.status === 'open') ?? [];
  const finished = data?.stocktakes.filter((s) => s.status === 'finished') ?? [];
  const alreadyOpen = farm
    ? open.find((s) => s.farm_id === farm.farm_id && s.block_id === (blockId || null) && kindOf(s) === kind)
    : undefined;

  const start = async () => {
    if (!farmId) return;
    setStarting(true);
    setError(null);
    try {
      const res = await stocktakeService.start(farmId, blockId || null, kind);
      if (!res.success) throw new Error(errorMessage(res, 'Could not start the count'));
      navigate(`/nursery/stocktake/${res.data.id}`);
    } catch (e) {
      setError(errorMessage(e, 'Could not start the count'));
      setStarting(false);
    }
  };

  if (licenseLoading) {
    return <div className="max-w-2xl mx-auto px-4 py-6"><div className="h-40 bg-neutral-100 rounded animate-pulse" /></div>;
  }
  if (!hasAccess) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12">
        <div className="bg-white border border-neutral-200 rounded-xl p-8 text-center">
          <Lock className="h-10 w-10 text-neutral-300 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-neutral-800 mb-1">Nursery seat required</h2>
          <p className="text-sm text-neutral-500">Counting stock needs a nursery seat. The free Order Picker seat is enough.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <PageHeader title="Stocktake" subtitle="Walk the rows and count the lots" icon={<ClipboardCheck className="h-6 w-6 text-white" />} />
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {error && <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 break-words">{error}</div>}
        {!data ? (
          !error && <div className="h-40 bg-neutral-100 rounded-xl animate-pulse" />
        ) : data.farms_assigned === 0 ? (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 text-sm text-amber-900">
            You are not assigned to any farm yet, so there is nothing for you to count. Ask a nursery manager to choose
            your farms on the Order Pickers page.
          </div>
        ) : (
          <>
            <section className="bg-white border border-neutral-200 rounded-xl p-4 space-y-3" aria-label="Start a count">
              <h2 className="font-semibold text-neutral-900">Start a count</h2>
              {data.places.length === 0 ? (
                <p className="text-sm text-neutral-500">No lots are placed at a farm yet. Place lots on the Locations page first.</p>
              ) : (
                <>
                  <div className="text-sm min-w-0">
                    <label htmlFor="stocktake-kind" className="text-neutral-700">What are you counting?</label>
                    <select
                      id="stocktake-kind"
                      className="mt-1 w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm"
                      value={kind}
                      onChange={(e) => setKind(e.target.value as StocktakeKind)}
                    >
                      {(Object.keys(STOCKTAKE_KIND_NAME) as StocktakeKind[]).map((k) => (
                        <option key={k} value={k}>{STOCKTAKE_KIND_NAME[k]}</option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-neutral-500 break-words">{KIND_HELP[kind]}</p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="text-sm min-w-0">
                      <label htmlFor="stocktake-farm" className="text-neutral-700">Farm</label>
                      <select
                        id="stocktake-farm"
                        className="mt-1 w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm"
                        value={farmId}
                        onChange={(e) => { setFarmId(e.target.value ? Number(e.target.value) : ''); setBlockId(''); }}
                      >
                        <option value="">Choose a farm</option>
                        {data.places.map((p) => (
                          <option key={p.farm_id} value={p.farm_id}>{p.farm_name} ({p.lots} lots)</option>
                        ))}
                      </select>
                    </div>
                    <div className="text-sm min-w-0">
                      <label htmlFor="stocktake-block" className="text-neutral-700">Block</label>
                      <select
                        id="stocktake-block"
                        className="mt-1 w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm"
                        value={blockId}
                        disabled={!farm}
                        onChange={(e) => setBlockId(e.target.value ? Number(e.target.value) : '')}
                      >
                        <option value="">Every block</option>
                        {farm?.blocks.map((b) => (
                          <option key={b.block_id} value={b.block_id}>{b.block_name} ({b.lots} lots)</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-3">
                    <Button onClick={start} disabled={!farmId || starting}>
                      {starting ? 'Starting…' : alreadyOpen ? 'Join the count' : 'Start counting'}
                    </Button>
                    {alreadyOpen && (
                      <span className="text-sm text-neutral-600 break-words min-w-0">
                        {STOCKTAKE_KIND_NAME[kind]} {alreadyOpen.label} is already under way here
                        {alreadyOpen.started_by_name ? `, started by ${alreadyOpen.started_by_name}` : ''}. You will count with them.
                      </span>
                    )}
                  </div>
                </>
              )}
            </section>

            {open.length > 0 && (
              <section aria-label="Counting now">
                <h2 className="text-sm font-semibold text-neutral-700">Counting now</h2>
                <ul className="mt-2 space-y-3">{open.map((s) => <StocktakeRow key={s.id} s={s} tz={tz} />)}</ul>
              </section>
            )}
            {finished.length > 0 && (
              <section aria-label="Finished">
                <h2 className="text-sm font-semibold text-neutral-700">Finished</h2>
                <ul className="mt-2 space-y-3">{finished.map((s) => <StocktakeRow key={s.id} s={s} tz={tz} />)}</ul>
              </section>
            )}
          </>
        )}
      </div>
    </>
  );
}

export default Stocktakes;
